<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form v-model="formData.valid" class="mb-3" validate-on="input" @submit.prevent="submitForm">
            <v-card style="position: relative;">
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4 v-if="otaCampaign">Update OTA Campaign</h4>
                    <h4 v-else>Add New OTA Campaign</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>
                <v-overlay :model-value="!formReady" contained class="align-center justify-center">
                    <v-progress-circular indeterminate color="primary"/>
                </v-overlay>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['name']" autofocus :rules="[vRules.required]" density="compact"
                                          label="Campaign Name"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['max_retries']" :rules="[vRules.required]"
                                          density="compact" label="Max Retries" min="0" type="number"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-text-field v-model="formData['retry_interval']" :rules="[vRules.required]"
                                          density="compact" label="Retry Interval" min="0" type="number"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['ota_package']" :items="otaPackages"
                                      :rules="[vRules.required]" item-title="label" item-value="id"
                                      density="compact" label="OTA Package"/>
                        </v-col>

                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['device_group']" :items="deviceGroups"
                                      :rules="[vRules.required]" item-title="label" item-value="id"
                                      density="compact" label="Device Group"/>
                        </v-col>

                        <v-col class="pb-0" cols="12">
                            <v-select v-model="formData['status']"
                                      :items="[{value:1, title: 'Enabled'},{value:0, title: 'Disabled'}]"
                                      :rules="[vRules.required]"
                                      density="compact" label="Status"/>
                        </v-col>
                        <v-col class="pb-0" cols="12">
                            <v-textarea v-model="formData['notes']" label="Notes" rows="2" auto-grow density="compact"/>
                        </v-col>
                    </v-row>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button"
                           @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";

const formReady = ref(false);
const snackbar = useSnackbarStore();
const props = defineProps({
    modelValue: Boolean,
    otaCampaign: Object,
});
const formData = reactive({
    valid: false,
    ota_package: props.otaCampaign?.ota_package_id ?? null,
    device_group: props.otaCampaign?.device_group_id ?? null,
    max_retries: props.otaCampaign?.max_retries ?? null,
    retry_interval: props.otaCampaign?.retry_interval ?? null,
    status: props.otaCampaign?.status ?? null,
    name: props.otaCampaign?.name ?? null,
    notes: props.otaCampaign?.notes ?? null,
});
const emits = defineEmits(['update:modelValue', 'addCampaign', 'updateCampaign']);
const dialogSwitch = ref(props.modelValue);
const deviceGroups = ref([]);
const otaPackages = ref([]);
const loading = ref(true);
const errors = ref([]);

onMounted(() => {
    Promise.all([fetchPackagesList(), fetchDeviceGroups()])
        .finally(() => formReady.value = true);
})
const fetchPackagesList = () => {
    loading.value = true;
    return send('get', `admin/ota/packages`, {list: true})
        .then((response) => {
            otaPackages.value = response.data.data;
        }).catch((error) => {
            snackbar.showSnackbar('OTA Campaigns list could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
const fetchDeviceGroups = () => {
    loading.value = true;
    return send('get', `device-groups`, {list: true})
        .then((response) => {
            deviceGroups.value = response.data.data.device_groups;
        }).catch((error) => {
            snackbar.showSnackbar('Device Groups could not be loaded', 'error')
        }).finally(() => loading.value = false)
}
const submitForm = () => {
    errors.value = [];
    if (props.otaCampaign) {
        send('put', `admin/ota/campaigns/${props.otaCampaign.id}`, formData)
            .then((response) => {
                snackbar.showSnackbar(response.data.message);
                if (response.data.success) {
                    emits('updateCampaign', response.data.data);
                    emits('update:modelValue', false);
                }
            })
            .catch(function (errorResponse) {
                if (errorResponse.response?.data?.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response?.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                snackbar.showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            })
    } else {
        send('post', `admin/ota/campaigns`, formData)
            .then((response) => {
                snackbar.showSnackbar(response.data.message);
                if (response.data.success) {
                    emits('addCampaign', response.data.data);
                    emits('update:modelValue', false);
                }
            })
            .catch(function (errorResponse) {
                console.log(errorResponse);
                if (errorResponse.response?.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response?.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                snackbar.showSnackbar('Something went wrong', 'error');
                document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
            })
    }
}

</script>
